import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { UtilPopupService } from '../util-popup.service';

@Injectable({
    providedIn: 'root',
})
export class ExportToExcelService {
    /** Formato con el que Excel muestra las celdas de fecha del reporte. */
    private readonly FORMATO_FECHA_EXCEL = 'dd/mm/yyyy';

    /** Columnas que el backend entrega como texto 'DD/MM/YYYY'. */
    private readonly COLUMNAS_FECHA_DMY = ['fecha_carga', 'fecha_ingreso'];

    /** Última respuesta de la búsqueda de control de afiliación (en memoria, sin límite de tamaño). */
    private respuestaBusqueda: any = null;

    constructor(
        private utilPopupService: UtilPopupService
    ) { }

    /**
     * Guarda la respuesta de la búsqueda para exportarla después.
     *
     * @param respuesta - Respuesta completa del endpoint de búsqueda.
     */
    guardarRespuesta(respuesta: any): void {
        this.respuestaBusqueda = respuesta;
    }

    /**
     * Method to flatten an array of objects.
     *
     * This method flattens an array of objects by converting nested properties into a single level of properties.
     * It also processes specific properties ('AFP', 'ARL', 'CCF', 'EPS') to extract and format relevant information.
     *
     * @param data - The array of objects to be flattened.
     * @returns The flattened array of objects.
     */
    private flattenArray(data: any[]): any[] {
        const flattenedData: any[] = [];
        const tiposEntidad = ['AFP', 'ARL', 'CCF', 'EPS'];

        for (const obj of data) {
            let baseRow = this.flattenObject(obj);
            // Iterate through each entity type
            for (const i of tiposEntidad) {
                if (obj.hasOwnProperty(i) && obj[i].length > 0) {
                    for (const entity of obj[i]) {
                        let entityRow = { ...baseRow }; // Create a copy of the base row
                        // Add entity-specific information
                        entityRow['Tipo Entidad'] = this.tipoEntidad(entity.id_tipo_entidad);
                        entityRow['Entidad'] = entity.nombre ?? '';
                        entityRow['Tipo de novedad'] = entity.tipo_novedad ?? '';
                        entityRow['Estado de afiliación'] = entity.valor_estado ?? '';
                        entityRow['Motivo'] = entity.motivo ?? '';
                        const demora = entity.motivo_demora;
                        entityRow['Motivo de demora'] = demora
                            ? [demora.nombre, demora.observacion].filter(Boolean).join(' - ')
                            : '';
                        entityRow['Fecha motivo de demora'] = demora ? this.aFechaDesdeDMY(demora.fecha_registro) : '';
                        const fechaCreacion = entity.trazabilidad?.fecha_creacion;
                        entityRow['Fecha ultimo estado'] = this.aFechaDesdeIso(fechaCreacion);
                        entityRow['nivel_1'] = obj.nivel_1 ?? '';
                        entityRow['nivel_2'] = obj.nivel_2 ?? '';
                        entityRow['nivel_3'] = obj.nivel_3 ?? '';
                        entityRow['nivel_4'] = obj.nivel_4 ?? '';
                        // Add the row to the flattened data array
                        flattenedData.push(entityRow);
                    }
                }
            }

            // Check if the 'beneficiarios' property exists and is not empty.
            baseRow['beneficiarios'] = obj?.beneficiarios?.length > 0 ? 'Yes' : 'No';
        }

        return flattenedData;
    }

    /**
     * Convierte una fecha ISO 8601 ('2026-08-05T05:28:05.382449') en un objeto Date
     * para que Excel la reciba como fecha real y no como texto.
     *
     * Se toma la parte de la fecha del string tal cual viene del backend (sin
     * reinterpretar zona horaria), de modo que el dia mostrado sea el mismo que
     * se ve en pantalla.
     *
     * @param fechaCreacion - Fecha en formato ISO 8601.
     * @returns Date con la fecha, o '' si no viene informacion.
     */
    aFechaDesdeIso(fechaCreacion: string): Date | string {
        if (!fechaCreacion) {
            return '';
        }
        const [anio, mes, dia] = String(fechaCreacion).slice(0, 10).split('-').map(Number);
        if (!anio || !mes || !dia) {
            return '';
        }
        return new Date(anio, mes - 1, dia);
    }

    /**
     * Convierte una fecha en texto 'DD/MM/YYYY' (como la entrega el backend en
     * fecha_carga / fecha_ingreso) en un objeto Date para exportarla como fecha.
     *
     * @param fecha - Fecha en formato 'DD/MM/YYYY'.
     * @returns Date con la fecha, o el valor original si no tiene ese formato.
     */
    aFechaDesdeDMY(fecha: any): any {
        if (typeof fecha !== 'string' || !/^\d{2}\/\d{2}\/\d{4}$/.test(fecha)) {
            return fecha ?? '';
        }
        const [dia, mes, anio] = fecha.split('/').map(Number);
        return new Date(anio, mes - 1, dia);
    }

    tipoEntidad(idTipoEntidad: number) {
        switch (idTipoEntidad) {
            case 1: return 'ARL'
            case 2: return 'AFP'
            case 3: return 'CCF'
            case 4: return 'EPS'
        }
    }

    /**
     * Method to flatten a JSON object based on key-value pairs.
     *
     * This method flattens a JSON object by converting nested properties into a single level of properties.
     * It includes only the keys that are specified in the predefined columns array.
     *
     * @param obj - The JSON object to be flattened.
     * @returns The flattened JSON object.
     */
    private flattenObject(obj: any): any {
        const flattenedRow: any = {};

        // Define the desired columns to be included in the flattened object.
        const columns = [
            'id_afiliacion', 'fecha_carga', 'fecha_ingreso', 'numero_contrato', 'tipo_documento', 'documento',
            'nombre_empleado', 'empresa', 'nit', 'partner'
        ];

        /**
         * Recursive function to flatten the object.
         *
         * @param obj - The object to flatten.
         * @param parentKey - The key of the parent object.
         */
        function flatten(obj: any, parentKey = '') {
            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    const value = obj[key];
                    const currentKey = parentKey ? `${parentKey}_${key}` : key;

                    // Recursively flatten nested objects.
                    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                        flatten(value, currentKey);
                    } else if (columns.includes(currentKey)) {
                        // Include the key only if it's in the columns array.
                        flattenedRow[currentKey] = value;
                    }
                }
            }
        }

        // Call the recursive flatten function.
        flatten(obj);

        // Ensure the flattenedRow object respects the order defined in the columns array.
        const orderedFlattenedRow: any = {};
        for (const column of columns) {
            if (flattenedRow.hasOwnProperty(column)) {
                // Las columnas de fecha se exportan como Date para que Excel las
                // reconozca como fecha y no como texto.
                orderedFlattenedRow[column] = this.COLUMNAS_FECHA_DMY.includes(column)
                    ? this.aFechaDesdeDMY(flattenedRow[column])
                    : flattenedRow[column];
            }
        }

        return orderedFlattenedRow;
    }

    /**
     * Convierte un Date (medianoche en hora local) al numero serial de Excel.
     *
     * El calculo se hace a mano en vez de dejarselo a la libreria porque esta
     * ultima ajusta el serial con getTimezoneOffset(), que redondea a minutos el
     * offset historico de la zona horaria. En America/Bogota (LMT -04:56:16) se
     * pierden 16 segundos, el serial queda en 46219.9998 en vez de 46220 y Excel
     * trunca hacia abajo, mostrando el dia anterior.
     *
     * @param fecha - Fecha construida con new Date(anio, mes - 1, dia).
     * @returns Serial de Excel (dias desde 1899-12-30), siempre entero.
     */
    private aSerialExcel(fecha: Date): number {
        const MS_POR_DIA = 24 * 60 * 60 * 1000;
        // 25569 = dias entre el origen de Excel (1899-12-30) y el epoch de JS.
        return Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()) / MS_POR_DIA + 25569;
    }

    /**
     * Reemplaza las celdas de tipo fecha por su serial de Excel con formato
     * dd/mm/yyyy, para que Excel las reconozca como fecha real y muestre
     * exactamente el mismo dia que entrega el backend.
     *
     * @param worksheet - Hoja generada a partir de los datos aplanados.
     */
    private aplicaFormatoFechas(worksheet: XLSX.WorkSheet): void {
        for (const direccion of Object.keys(worksheet)) {
            if (direccion.startsWith('!')) {
                continue;
            }
            const celda = worksheet[direccion];
            if (celda && celda.t === 'd' && celda.v instanceof Date) {
                celda.t = 'n';
                celda.v = this.aSerialExcel(celda.v);
                celda.z = this.FORMATO_FECHA_EXCEL;
                delete celda.w;
            }
        }
    }

    /**
     * Method to export data to an Excel file.
     *
     * This method takes the last search response kept in memory, flattens the array of objects,
     * and exports the data to an Excel file. If any errors occur during the process, it shows an error message.
     */
    exportToExcel(): void {
        const data = this.respuestaBusqueda;

        // Check if a search response exists.
        if (data != null) {
            try {
                // Check if data is present and is an array.
                if (data && Array.isArray(data.afiliaciones)) {
                    // Flatten the array of objects.
                    const flattenedData: any[] = this.flattenArray(data.afiliaciones); // Call the flattening method

                    // Convert flattened data into an Excel worksheet.
                    // cellDates + dateNF: las columnas de fecha quedan como fecha
                    // real de Excel con formato dd/mm/yyyy (antes salian como texto).
                    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(flattenedData, {
                        cellDates: true,
                        dateNF: this.FORMATO_FECHA_EXCEL,
                    });
                    this.aplicaFormatoFechas(worksheet);
                    const workbook: XLSX.WorkBook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

                    // Download the Excel file.
                    XLSX.writeFile(workbook, 'afiliaciones.xlsx');
                } else {
                    // Show an error message if the array of affiliations is not found.
                    this.utilPopupService.mostrarMensaje(
                        'Ocurrió un error al realizar la descarga del reporte',
                        'error',
                        '',
                        false
                    );
                    console.error("The array of affiliations was not found in the JSON.");
                }
            } catch (error) {
                // Show an error message if building the file fails.
                this.utilPopupService.mostrarMensaje(
                    'Ocurrió un error al realizar la descarga del reporte',
                    'error',
                    '',
                    false
                );
                console.error("Error generating Excel:", error);
            }
        }
    }

}
